import type { ItemId, WheelSeatView } from '@goodtime/shared';

/**
 * Spotting a Reel Race gift (an item drop) in the room's state stream, so the
 * reel opens it and the loadout strip shows it. Pure, so the cases that lost
 * gifts are tested without a browser; {@link WheelReelRaceView} plays them.
 *
 * <p>A drop lands as a bigger loadout, or, with all three slots full, as
 * {@code lastSpinPendingItem} (the "tap to replace" offer). Two cases were
 * missed (producer 2026-11-03: gifts that "wouldn't open, and wouldn't show
 * what the gift was"; a slot that looked empty but held an item):
 * <ul>
 *   <li>the first spin after the race started, which the view took as its
 *       starting snapshot, so the gift never opened and its slot stayed
 *       empty all round;</li>
 *   <li>the same item dropping on two spins running with the slots full:
 *       the pending item did not change, so the second gift never opened
 *       and offered no swap.</li>
 * </ul>
 * Each seat's spin is told apart by its result, so a new drop is seen even
 * when it is the same item as the one before.
 */

/** What the view last saw of a seat. */
export interface SeatDropMark {
  loadoutLength: number;
  pending: ItemId | null;
  /** The seat's last spin result; changes with every spin it makes. */
  spin: string;
}

/** A gift that dropped on a seat's latest spin. */
export interface ItemDrop {
  itemId: ItemId;
  /** True when it went into an empty slot; false when the slots were full
   *  and it waits as the pending "tap to replace" item. */
  toLoadout: boolean;
}

export function markSeat(seat: WheelSeatView): SeatDropMark {
  return {
    loadoutLength: seat.loadout.length,
    pending: seat.lastSpinPendingItem,
    spin: [
      seat.tokens,
      seat.lastSpinGrid.join(','),
      seat.lastSpinBonusCells.join(','),
      seat.lastSpinItemCell ?? '',
    ].join('|'),
  };
}

/** The gift that dropped since {@code prev}, if any. With no {@code prev}
 *  (the seat's first sighting) there is nothing to compare, so nothing. */
export function detectItemDrop(
  prev: SeatDropMark | undefined,
  seat: WheelSeatView,
): ItemDrop | null {
  if (!prev) return null;
  if (seat.loadout.length > prev.loadoutLength) {
    const itemId = seat.loadout[seat.loadout.length - 1];
    return itemId ? { itemId, toLoadout: true } : null;
  }
  const pending = seat.lastSpinPendingItem;
  if (pending === null) return null;
  const spun = markSeat(seat).spin !== prev.spin;
  if (pending !== prev.pending || (spun && seat.lastSpinItemCell !== null)) {
    return { itemId: pending, toLoadout: false };
  }
  return null;
}
